// frontend/src/components/guards/RequireCurrentConsents.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Navigate, Outlet, useLocation } from "react-router-dom";

import { useAuth } from "../../AuthContext";
import { getMyConsentGateStatus } from "../../lib/legal/consent";

const CONSENT_CACHE_PREFIX = "gwin_current_consents_ok_session_v1";

function makeConsentCacheKey(userId) {
  if (!userId) return null;
  return `${CONSENT_CACHE_PREFIX}:${userId}`;
}

function readCachedConsentOk(userId) {
  try {
    if (typeof window === "undefined") return false;

    const cacheKey = makeConsentCacheKey(userId);
    if (!cacheKey) return false;

    return window.sessionStorage.getItem(cacheKey) === "true";
  } catch (error) {
    console.warn("Could not read cached consent status:", error);
    return false;
  }
}

function writeCachedConsentOk(userId) {
  try {
    if (typeof window === "undefined") return;

    const cacheKey = makeConsentCacheKey(userId);
    if (!cacheKey) return;

    window.sessionStorage.setItem(cacheKey, "true");
  } catch (error) {
    console.warn("Could not cache consent status:", error);
  }
}

export function clearCurrentConsentSessionCache(userId) {
  try {
    if (typeof window === "undefined") return;

    const prefix = userId
      ? `${CONSENT_CACHE_PREFIX}:${userId}`
      : CONSENT_CACHE_PREFIX;

    Object.keys(window.sessionStorage).forEach((key) => {
      if (key.startsWith(prefix)) {
        window.sessionStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn("Could not clear consent cache:", error);
  }
}

export default function RequireCurrentConsents() {
  const location = useLocation();
  const { user } = useAuth();

  const cachedConsentOk = useMemo(
    () => readCachedConsentOk(user?.id),
    [user?.id]
  );

  const [loading, setLoading] = useState(!cachedConsentOk);
  const [requiresReview, setRequiresReview] = useState(false);

  const checkConsentStatus = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      setRequiresReview(true);
      return;
    }

    const cachedOk = readCachedConsentOk(user.id);

    if (cachedOk) {
      setLoading(false);
      setRequiresReview(false);
      return;
    }

    try {
      setLoading(true);

      const status = await getMyConsentGateStatus();

      const nextRequiresReview = status.requiresReview === true;

      setRequiresReview(nextRequiresReview);

      if (!nextRequiresReview) {
        writeCachedConsentOk(user.id);
      }
    } catch (error) {
      console.error("Could not verify current consent status:", error);

      // Fail closed:
      // consent cannot be assumed when the check fails.
      setRequiresReview(true);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    checkConsentStatus();
  }, [checkConsentStatus]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FFF7ED] px-6">
        <p className="text-sm font-semibold text-[#757575]">
          Checking your privacy settings...
        </p>
      </div>
    );
  }

  if (requiresReview) {
    const returnTo = location.pathname + location.search + location.hash;

    return (
      <Navigate
        replace
        to="/profile-gate/consent-notices"
        state={{
          returnTo,
          consentReason: "updated",
        }}
      />
    );
  }

  return <Outlet />;
}